/**
 * useTokenRefresh Hook
 * 
 * Professional proactive token refresh implementation.
 * Automatically refreshes JWT tokens before they expire to ensure
 * seamless user experience without interruption.
 * 
 * Features:
 * - Proactive refresh (before token expires)
 * - Silent background refresh
 * - Automatic retry on failure
 * - Cleanup on unmount
 * 
 * @author Kuppion
 * @version 1.0.0
 */

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAuthenticated, logout } from '../store/slices/authSlice';
import AuthService from '../services/authService';
import TokenService from '../services/tokenService';

/**
 * Hook for automatic token refresh management
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable auto-refresh (default: true)
 * @param {number} options.maxRetries - Maximum retry attempts on failure (default: 3)
 */
export const useTokenRefresh = (options = {}) => {
  const { enabled = true, maxRetries = 3 } = options;
  
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const retryCountRef = useRef(0);
  const isRefreshingRef = useRef(false);

  /**
   * Perform token refresh
   */
  const refreshToken = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (isRefreshingRef.current) {
      console.log('🔄 useTokenRefresh: Refresh already in progress, skipping...');
      return false;
    }

    // Check if refresh token is still valid
    if (TokenService.isRefreshTokenExpired()) {
      console.log('🔐 useTokenRefresh: Refresh token expired, logging out...');
      dispatch(logout());
      return false;
    }

    isRefreshingRef.current = true;

    try {
      console.log('🔄 useTokenRefresh: Starting silent token refresh...');
      
      const result = await AuthService.refreshToken();
      
      if (result && result.token) {
        console.log('✅ useTokenRefresh: Token refreshed successfully');
        retryCountRef.current = 0;
        
        // Setup next proactive refresh
        TokenService.setupProactiveRefresh(() => refreshToken());
        
        return true;
      }
      
      throw new Error('Invalid refresh response');
    } catch (error) {
      console.error('❌ useTokenRefresh: Token refresh failed:', error);
      
      retryCountRef.current++;
      
      if (retryCountRef.current >= maxRetries) {
        console.log('🔐 useTokenRefresh: Max retries reached, logging out...');
        dispatch(logout());
        return false;
      }
      
      // Retry after a delay (exponential backoff)
      const retryDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
      console.log(`🔄 useTokenRefresh: Retrying in ${retryDelay}ms (attempt ${retryCountRef.current}/${maxRetries})`);
      
      setTimeout(() => {
        isRefreshingRef.current = false;
        refreshToken();
      }, retryDelay);
      
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [dispatch, maxRetries]);

  /**
   * Setup proactive refresh on mount and when authentication changes
   */
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      TokenService.clearProactiveRefresh();
      return;
    }

    // Check if token needs immediate refresh
    const token = TokenService.getAccessToken();
    if (token && TokenService.shouldRefreshToken(token)) {
      console.log('🔄 useTokenRefresh: Token needs immediate refresh');
      refreshToken();
    } else {
      // Setup proactive refresh timer
      TokenService.setupProactiveRefresh(() => refreshToken());
    }

    // Cleanup on unmount
    return () => {
      TokenService.clearProactiveRefresh();
    };
  }, [enabled, isAuthenticated, refreshToken]);

  /**
   * Handle visibility change (tab focus)
   * Refresh token when user returns to tab after being away
   */
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const token = TokenService.getAccessToken();
        
        // Check if token expired while tab was hidden
        if (token && TokenService.isTokenExpired(token)) {
          console.log('🔄 useTokenRefresh: Token expired while away, refreshing...');
          refreshToken();
        } else if (token && TokenService.shouldRefreshToken(token)) {
          console.log('🔄 useTokenRefresh: Token needs refresh after returning to tab');
          refreshToken();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isAuthenticated, refreshToken]);

  return {
    refreshToken,
    isRefreshing: isRefreshingRef.current
  };
};

export default useTokenRefresh;
